/**
 * Add Device Form Composer
 *
 * Form for registering new devices with cascading dropdowns.
 * - Manufacturer selection filters available device types
 * - deviceNumber is auto-generated on submission
 *
 * Form metadata and field configurations are loaded from form.yml
 */

import * as React from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  AddDeviceFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddDeviceFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderSelectControl,
  renderDateControl,
  renderRadioControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import {
  filterDeviceTypesByManufacturer,
  findItemById,
} from '@/lib/dataTransformationUtils'
import { useSelectOptionsMap } from '@/hooks/useSelectOptions'
import { coerceFieldValue } from '@/lib/utils'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Cpu } from 'lucide-react'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Component Props
// ============================================================================

export type AddDeviceComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<AddDeviceFormData>
  showHeader?: boolean
  submitLabel?: string
}

// ============================================================================
// Field validation via Zod schema (onBlur only)
// ============================================================================

const validateField = (name: keyof AddDeviceFormData, value: unknown): string | null => {
  if (value === undefined || value === null || value === '') return null
  const shape = AddDeviceFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// ============================================================================
// Composer Component
// ============================================================================

export function AddDeviceComposer(props: AddDeviceComposerProps) {
  const { onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  // Track selected manufacturer for filtering device types
  const [selectedManufacturer, setSelectedManufacturer] = React.useState<number | undefined>(
    initialValues?.manufacturerNumber
  )

  // When the user picks a device type we auto-sync the manufacturer. That manufacturer
  // change must NOT clear the device type the user just selected, so we flag the sync.
  const isSyncingManufacturerFromDeviceType = React.useRef(false)

  // Get field configurations from YAML
  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequiredField = (name: string) => {
    // Align with backend: upsertDevice requires ONLY device.deviceStatus.
    // deviceName, deviceTypeNumber, and all procurementInfo fields are optional there.
    return ['deviceStatus'].includes(name)
  }

  // Fetch all select options with a single hook call
  const { optionsMap, loadingMap, rawData } = useSelectOptionsMap({
    manufacturerNumber: {
      endpoint: 'getDeviceManufacturers',
      dataKey: 'manufacturers',
      valueField: 'manufacturerNumber',
      labelField: 'manufacturerName',
    },
    deviceTypeNumber: {
      endpoint: 'getDeviceTypes',
      dataKey: 'deviceTypes',
      valueField: 'deviceTypeNumber',
      labelField: 'deviceTypeName',
      // Use dependent transformer for cascading dropdown (overrides valueField/labelField)
      dependentTransformer: (data, deps) =>
        filterDeviceTypesByManufacturer(data, deps?.selectedManufacturer),
    },
    distributorNumber: {
      endpoint: 'getDeviceDistributors',
      dataKey: 'distributors',
      valueField: 'distributorNumber',
      labelField: 'distributorName',
    },
    deviceProfileNumber: {
      endpoint: 'getDeviceProfiles',
      dataKey: 'deviceProfiles',
      valueField: 'deviceProfileNumber',
      labelField: 'deviceProfileName',
    },
  }, { selectedManufacturer })

  // Get raw device types data for cascading logic
  const deviceTypesData = rawData.deviceTypeNumber || []

  const upsertDevice = useApiMutation('upsertDevice', 'POST', {
    invalidates: ['getDevices'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to create device:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: AddDeviceFormSchema,
    defaultValues: {
      deviceName: initialValues?.deviceName ?? '',
      manufacturerNumber: initialValues?.manufacturerNumber ?? undefined,
      deviceTypeNumber: (initialValues?.deviceTypeNumber ?? undefined) as number,
      deviceProfileNumber: initialValues?.deviceProfileNumber ?? undefined,
      distributorNumber: initialValues?.distributorNumber ?? undefined,
      deviceStatus: initialValues?.deviceStatus ?? 'ordered',
      devicePurchaseOrderNumber: initialValues?.devicePurchaseOrderNumber ?? undefined,
      deviceOrderDate: initialValues?.deviceOrderDate ?? undefined,
    },
    onSubmit: async ({ value }) => {
      const formValue = value as AddDeviceFormData
      // Coerce and validate with Zod schema (single source of truth)
      const coerced = {
        deviceName: formValue.deviceName?.trim() ?? '',
        manufacturerNumber: formValue.manufacturerNumber ? Number(formValue.manufacturerNumber) : undefined,
        deviceTypeNumber: formValue.deviceTypeNumber ? Number(formValue.deviceTypeNumber) : undefined,
        deviceProfileNumber: formValue.deviceProfileNumber ? Number(formValue.deviceProfileNumber) : undefined,
        distributorNumber: formValue.distributorNumber ? Number(formValue.distributorNumber) : undefined,
        deviceStatus: formValue.deviceStatus || undefined,
        devicePurchaseOrderNumber: formValue.devicePurchaseOrderNumber ? Number(formValue.devicePurchaseOrderNumber) : undefined,
        deviceOrderDate: formValue.deviceOrderDate || undefined,
      }

      const parsed = AddDeviceFormSchema.safeParse(coerced)

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as keyof AddDeviceFormData, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      // Transform and submit
      const apiRequest = transformToApiRequest(parsed.data)
      await upsertDevice.mutateAsync(apiRequest)
    },
  })

  // Subscribe to orderedFrom field value for conditional rendering
  const orderedFrom = form.useStore((s: any) => s.values.orderedFrom as 'manufacturer' | 'distributor' | undefined)

  // Validate on mount to ensure submit button is disabled if form is invalid
  React.useEffect(() => {
    // We can't easily validate everything without triggering errors,
    // but since we updated fields to only show errors on touch,
    // we can safely trigger validation here to populate the error state
    // which the SubmitButton uses to disable itself.
    // However, tanstack-form doesn't have a simple "validate silently" method exposed here easily
    // without manually running the schema check.
    // But wait, the SubmitButton checks `state.errors`.
    // If we want it disabled, we need errors.

    // Let's just trigger a validation run.
    // form.validate() is available on the form instance.
    // form.validate('mount')
  }, [])

  // Handle manufacturer change to filter device types (cascading dropdown)
  const handleManufacturerChange = (value: string) => {
    const numValue = value ? Number(value) : undefined
    setSelectedManufacturer(numValue)

    // This manufacturer change was triggered by the user selecting a device type —
    // keep that selection, don't clear it.
    if (isSyncingManufacturerFromDeviceType.current) {
      isSyncingManufacturerFromDeviceType.current = false
      return
    }

    // Check if current device type is valid for new manufacturer
    const currentDeviceTypeVal = form.getFieldValue('deviceTypeNumber')
    if (currentDeviceTypeVal) {
       const deviceType = findItemById(deviceTypesData, 'deviceTypeNumber', Number(currentDeviceTypeVal)) as any

       // If the current device type belongs to the new manufacturer, don't clear it.
       // Coerce both sides — the API may return manufacturer numbers as strings.
       if (deviceType && Number(deviceType.deviceTypeManufacturerNumber) === numValue) {
          return
       }
    }

    // Clear device type selection when manufacturer changes
    form.setFieldValue('deviceTypeNumber', undefined as any)
  }

  // Handle device type change (auto-update manufacturer)
  const handleDeviceTypeChange = (value: string) => {
    // If user selects a device type, update manufacturer dropdown to match
    if (value && deviceTypesData.length > 0) {
      const selectedType = findItemById(
        deviceTypesData,
        'deviceTypeNumber',
        Number(value)
      ) as any
      if (selectedType?.deviceTypeManufacturerNumber) {
        // Flag so the resulting manufacturer change doesn't wipe this selection.
        isSyncingManufacturerFromDeviceType.current = true
        setSelectedManufacturer(Number(selectedType.deviceTypeManufacturerNumber))
        form.setFieldValue('manufacturerNumber', selectedType.deviceTypeManufacturerNumber as any)
      }
    }
  }

  // Custom handlers for specific fields
  const customHandlers: Record<string, (value: string) => void> = {
    manufacturerNumber: handleManufacturerChange,
    deviceTypeNumber: handleDeviceTypeChange,
  }

  // Render a single text control
  const renderTextControlField = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(
              control.name as keyof AddDeviceFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            type: control.inputType as ControlConfig['type'] ?? 'text',
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Render a single select control with visibility and disabled states
  const renderSelectControlField = (control: any) => {
    const isRequired = isRequiredField(control.name)
    const isLoading = loadingMap[control.name]

    // Get options from API or YAML
    const options = control.optionsEndpoint
      ? optionsMap[control.name] || []
      : FormConfigLoader.normalizeSelectOptions(control.selectOptions)

    log.debug(`[AddDeviceComposer] Rendering select field "${control.name}":`, {
      hasEndpoint: !!control.optionsEndpoint,
      endpoint: control.optionsEndpoint,
      optionsFromMap: optionsMap[control.name]?.length ?? 0,
      optionsFromYAML: control.selectOptions?.length ?? 0,
      finalOptionsCount: options.length,
      isLoading,
      allOptionsMap: Object.keys(optionsMap).map(key => ({ key, count: optionsMap[key]?.length ?? 0 })),
    })

    const descriptionText: string =
      control.name === 'deviceTypeNumber' && selectedManufacturer
        ? `Showing device types from selected manufacturer (${options.length} available)`
        : control.name === 'deviceTypeNumber'
        ? 'All device types shown. Select a manufacturer to filter.'
        : control.description ?? ''

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(
              control.name as keyof AddDeviceFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
        }}
        listeners={customHandlers[control.name] ? {
          onChange: ({ value }) => customHandlers[control.name]?.(String(value ?? '')),
        } : undefined}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            description: descriptionText,
            options: options,
            isLoading: isLoading,
          }
          return renderSelectControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Render a single date control
  const renderDateControlField = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(
              control.name as keyof AddDeviceFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            type: 'date'
          }
          return renderDateControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Universal render function that dispatches to specific renderers
  const renderControl = (control: any) => {
    if (control.htmlControl === 'radio') {
      return renderRadioControlField(control)
    }
    if (control.htmlControl === 'select') {
      return renderSelectControlField(control)
    }
    if (control.htmlControl === 'date') {
      return renderDateControlField(control)
    }
    return renderTextControlField(control)
  }

  const renderRadioControlField = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(control.name as keyof AddDeviceFormData, value) || undefined,
        }}
        listeners={control.name === 'orderedFrom' ? {
          onChange: ({ value }) => {
            const next = value as 'manufacturer' | 'distributor'
            // Clear only the opposite source field when switching. Device Type is
            // now picked first (Feature 6), so it (and its derived manufacturer) is
            // preserved regardless of the ordered-from source.
            if (next === 'manufacturer') {
              form.setFieldValue('distributorNumber', undefined as any)
            } else {
              form.setFieldValue('manufacturerNumber', undefined as any)
              setSelectedManufacturer(undefined)
            }
          },
        } : undefined}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            options: control.selectOptions || [],
          }
          return renderRadioControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Render the manufacturer dropdown (when orderedFrom === 'manufacturer')
  const renderManufacturerField = () => (
    <form.AppField
      key="manufacturerNumber"
      name="manufacturerNumber"
      validators={{
        onBlur: ({ value }) => validateField('manufacturerNumber', coerceFieldValue('manufacturerNumber', value)) || undefined,
      }}
      listeners={{ onChange: ({ value }) => handleManufacturerChange(String(value ?? '')) }}
    >
      {(bound) => renderSelectControl({
        name: 'manufacturerNumber',
        label: 'Manufacturer',
        placeholder: 'Select manufacturer',
        // Optional per backend — manufacturer isn't part of the upsertDevice payload (UI-only).
        required: false,
        visibility: true,
        disabled: false,
        options: optionsMap['manufacturerNumber'] || [],
        isLoading: loadingMap['manufacturerNumber'],
      }, bound)}
    </form.AppField>
  )

  // Render the distributor dropdown (when orderedFrom === 'distributor')
  const renderDistributorField = () => (
    <form.AppField
      key="distributorNumber"
      name="distributorNumber"
      validators={{
        onBlur: ({ value }) => validateField('distributorNumber', coerceFieldValue('distributorNumber', value)) || undefined,
      }}
    >
      {(bound) => renderSelectControl({
        name: 'distributorNumber',
        label: 'Distributor',
        placeholder: 'Select distributor',
        // Optional per backend — procurementInfo (incl. deviceDistributorNumber) has no required fields.
        required: false,
        visibility: true,
        disabled: false,
        options: optionsMap['distributorNumber'] || [],
        isLoading: loadingMap['distributorNumber'],
      }, bound)}
    </form.AppField>
  )

  // Render the device profile dropdown (Feature 6). Optional — sent on the device;
  // backend persistence is pending the device→profile link hand-off.
  const renderDeviceProfileField = () => (
    <form.AppField
      key="deviceProfileNumber"
      name="deviceProfileNumber"
      validators={{
        onBlur: ({ value }) => validateField('deviceProfileNumber', coerceFieldValue('deviceProfileNumber', value)) || undefined,
      }}
    >
      {(bound) => renderSelectControl({
        name: 'deviceProfileNumber',
        label: 'Device Profile',
        placeholder: 'Select device profile',
        required: false,
        visibility: true,
        disabled: false,
        options: optionsMap['deviceProfileNumber'] || [],
        isLoading: loadingMap['deviceProfileNumber'],
      }, bound)}
    </form.AppField>
  )

  // Render form content. Order: Device Type → Device Profile (Feature 6) →
  // Ordered From + conditional Manufacturer/Distributor → the remaining fields.
  const renderFormContent = () => {
    const basicControls = controlsByGroup.get('basicInformationGroup') || []
    const classificationControls = controlsByGroup.get('classificationGroup') || []
    const byName = (list: any[], name: string) => list.find((c) => c.name === name)

    const deviceTypeControl = byName(classificationControls, 'deviceTypeNumber')
    const orderedFromControl = byName(basicControls, 'orderedFrom')
    // Everything else (name, PO, order date, status) renders after the primary group.
    const restControls = basicControls.filter((c) => c.name !== 'orderedFrom')

    return (
      <>
        {/* Primary group — the device's defining selections come first */}
        <div className="space-y-4">
          {deviceTypeControl && renderSelectControlField(deviceTypeControl)}
          {renderDeviceProfileField()}
          {orderedFromControl && renderControl(orderedFromControl)}
          {orderedFrom === 'manufacturer' && renderManufacturerField()}
          {orderedFrom === 'distributor' && renderDistributorField()}
        </div>
        {/* Remaining device details: name, PO number, order date, status */}
        <div className="space-y-4">
          {restControls.map(renderControl)}
        </div>
      </>
    )
  }

  const formBody = (
    <>
      {renderFormContent()}

      <div className="pt-4 flex justify-center gap-3">
        {renderButtonControl({
          label: FORM_CONFIG.cancelButton?.label ?? '',
          variant: 'outline',
          onClick: onCancel,
          visibility: !!onCancel,
          type: 'button'
        })}

        {renderButtonControl({
          label: upsertDevice.isPending
            ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Submitting...')
            : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Submit'),
          variant: 'default',
          type: 'submit'
        }, { SubmitButton: form.SubmitButton })}
      </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Cpu className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}

// Re-exports
export {
  AddDeviceFormSchema,
  transformToApiRequest,
  DeviceStatusEnum,
  FORM_CONFIG,
  type AddDeviceFormData,
  type DeviceStatus,
} from './schema'
